import { inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import { isPlatformBrowser } from '@angular/common';
import { ChunkedTokenStorage } from '../../utils/helpers/chunked-token-storage';
import { environment } from '../../../../environments/environment';

export enum ConnectionState {
  Disconnected = 'Disconnected',
  Connecting = 'Connecting',
  Connected = 'Connected',
  Reconnecting = 'Reconnecting',
  Disconnecting = 'Disconnecting'
}

@Injectable({
  providedIn: 'root',
})
export class SignalRService {
  private platformId = inject(PLATFORM_ID);
  private chunkedStorage = inject(ChunkedTokenStorage);
  
  private connection: HubConnection | null = null;
  private hubUrl: string = environment.notificationHub;
  private pingInterval: any = null;
  
  connectionState = signal<ConnectionState>(ConnectionState.Disconnected);
  private readonly PING_INTERVAL = 30000; // 30 seconds
  private readonly MAX_RECONNECT_ATTEMPTS = 5;
   
  createConnection(): HubConnection {
   
    if (!isPlatformBrowser(this.platformId)) {
      throw new Error('SignalR can only be used in browser environment');
    }

    // Check if token exists initially
    const initialToken = this.chunkedStorage.getToken('accessToken');
    if (!initialToken) {
      throw new Error('Access token not found');
    }

    const connection = new HubConnectionBuilder()
      .withUrl(this.hubUrl, {
        logMessageContent: true,
        skipNegotiation: false,
        
        accessTokenFactory: () => {
          const token = this.chunkedStorage.getToken('accessToken');
          if (!token) {
            throw new Error('Access token not available for SignalR connection');
          }
          return token;
        },
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          if (retryContext.previousRetryCount < this.MAX_RECONNECT_ATTEMPTS) {
            // Exponential backoff: 2s, 4s, 8s, 16s, 32s
            return Math.min(2000 * Math.pow(2, retryContext.previousRetryCount), 32000);
          }
          return null;
        }
      })
      .configureLogging(LogLevel.None)
      .build();

    this.setupConnectionHandlers(connection);
  
    this.setupPingPongHandlers(connection);

    this.connection = connection;
    
    return connection;
  }

  private setupConnectionHandlers(connection: HubConnection): void {
    connection.onclose((error) => {
      if (error) {
        // Check if it's an authentication error
        if (error.message?.includes('401') || error.message?.includes('Unauthorized')) {
          // Authentication failed
          console.log('Authentication failed');
        }
      }
      
      this.connectionState.set(ConnectionState.Disconnected);
      this.stopPingPong();
    });

    connection.onreconnecting((error) => {
      this.connectionState.set(ConnectionState.Reconnecting);
      this.stopPingPong();
    });

    connection.onreconnected((connectionId) => {
      this.connectionState.set(ConnectionState.Connected);
      this.startPingPong();
    });
  }

  private setupPingPongHandlers(connection: HubConnection): void {
    // Clear any existing ping interval
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  
    // Remove existing Pong listener to prevent duplicates
    connection.off('Pong');
  
    // Listen for Pong response from server
    connection.on('Pong', () => {
      // Pong received
    });
  }

  private startPingPong(): void {
    if (!this.connection || !this.isConnected() || this.connectionState() !== ConnectionState.Connected) {
      return;
    }

    this.stopPingPong();
    this.sendPing();

    this.pingInterval = setInterval(() => {
      if (!this.isConnected() || this.connectionState() !== ConnectionState.Connected) {
        this.stopPingPong();
        return;
      }
      this.sendPing();
    }, this.PING_INTERVAL);
  }

  private stopPingPong(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  private async sendPing(): Promise<void> {
    if (!this.connection || !this.isConnected() || this.connectionState() !== ConnectionState.Connected) {
      return;
    }

    const pingMethods = ['SendPing'];
    
    for (const method of pingMethods) {
      try {
        await this.connection.invoke(method);
        return;
      } catch (err: any) {
        if (
          err?.message?.includes('does not exist') ||
          err?.message?.includes('Unknown hub method')
        ) {
          continue;
        }
      }
    }
  }


  async startConnection(): Promise<void> {
    if (!this.connection) {
      throw new Error('Connection not created. Call createConnection first.');
    }

    if (this.connection.state === HubConnectionState.Connected) {
      return;
    }

    if (this.connection.state === HubConnectionState.Connecting) {
      return;
    }

    try {
      this.connectionState.set(ConnectionState.Connecting);
      
      await this.connection.start();
      
      this.connectionState.set(ConnectionState.Connected);
      this.startPingPong();
    } catch (error: any) {
      // Check for authentication errors
      if (error.statusCode === 401 || error.message?.includes('401') || error.message?.includes('Unauthorized')) {
        // Authentication failed
      }
      
      this.connectionState.set(ConnectionState.Disconnected);
      this.stopPingPong();
      throw error;
    }
  }

  async stopConnection(): Promise<void> {
    if (!this.connection || this.connection.state === HubConnectionState.Disconnected) {
      return;
    }

    try {
      this.connectionState.set(ConnectionState.Disconnecting);
      this.stopPingPong();
      await this.connection.stop();
    } catch (error) {
      // Error stopping connection
    } finally {
      this.connectionState.set(ConnectionState.Disconnected);
      this.connection = null;
    }
  }

  getConnection(): HubConnection | null {
    return this.connection;
  }

  isConnected(): boolean {
    return this.connection?.state === HubConnectionState.Connected;
  }

  async invoke<T>(methodName: string, ...args: any[]): Promise<T> {
    if (!this.connection || !this.isConnected()) {
      throw new Error('Connection not established');
    }
    return await this.connection.invoke<T>(methodName, ...args);
  }

  on(methodName: string, callback: (...args: any[]) => void): void {
    if (!this.connection) {
      throw new Error('Connection not established');
    }
    this.connection.on(methodName, callback);
  }

  off(methodName: string, callback?: (...args: any[]) => void): void {
    if (!this.connection) return;
    callback ? this.connection.off(methodName, callback) : this.connection.off(methodName);
  }

  /**
   * Manually attempt to reconnect after connection loss
   * Useful when automatic reconnect has exhausted all attempts
   */
  async reconnect(): Promise<void> {
    try {
      // Stop existing connection if any
      await this.stopConnection();
      
      // Create new connection with fresh token
      this.createConnection();
      
      // Start the connection
      await this.startConnection();
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get current connection state as ConnectionState enum
   */
  getConnectionState(): ConnectionState {
    return this.connectionState();
  }
}

